import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { DeskSession } from './useDeskSession'
import { hhmm } from '../../lib/useFictionalClock'
import { useKeyboardInset } from '../../lib/useVisualViewport'
import { Book } from './parts/Instruments'
import InstrumentStrip, { type Readout } from './parts/InstrumentStrip'
import Traffic from './parts/Traffic'
import Outbox from './parts/Outbox'
import Compose from './parts/Compose'
import Dossier, { cardsAsGiven } from './parts/Dossier'
import styles from './Mobile.module.css'
import { useT } from '../../lib/i18n'

/**
 * The desk, in a hand.
 *
 * A SIBLING OF Desktop.tsx OVER THE SAME HOOK, not a set of breakpoints inside
 * it — which is what the device seam exists for (lib/device.ts: "device class is
 * not a screen size, it is a different interaction model"). Every fact and every
 * act arrives already decided in `session`; nothing here knows anything the
 * desktop surface does not.
 *
 * THE ONE DECISION THIS FILE MAKES: on a desk the operator's book lies open
 * BESIDE the transcript, and both are legible at once. In a hand there is room
 * for one of them, and the transcript is the one you came for — so the book
 * becomes a sheet you pull up, and the reading surface gets the whole screen.
 *
 * That is a different arrangement of ELEMENTS, not a narrower version of the
 * same one, which is the test for whether a device branch has earned itself.
 * What follows from it:
 *
 *  - the header is a rail: back, station, clock, and the two things you summon;
 *  - the strip stays (it is thin, diegetic, and already a scrollable rail);
 *  - the composer is pinned to the bottom with the transmit key primary
 *    (§4.2 — this surface expects speaking to be the majority mobile input);
 *  - the book's sheet and the dossier are both full-surface, because a phone
 *    does not do two things at once.
 */
export default function DeskMobile({ session }: { session: DeskSession }) {
  const t = useT()
  const s = session
  const [sheet, setSheet] = useState<'none' | 'book' | 'paper'>('none')
  const [draft, setDraft] = useState('')
  const [composing, setComposing] = useState(false)

  /*
   * THE KEYBOARD INSET. The shell is sized off the room that actually exists
   * rather than off `100dvh`, so the surface SHRINKS when the keyboard opens
   * instead of being scrolled up and back down. The transcript is bottom-anchored,
   * so his last lines stay exactly where they were while everything above them
   * simply has less room.
   */
  const keyboard = useKeyboardInset()

  /**
   * The last thing HE sent — what a transmission is answering.
   *
   * Protocol lines are excluded: "sked open 10:41 — link established" is the
   * machine's own voice, and pinning it as the thing you are answering would be
   * the surface inventing a conversational turn that never happened.
   */
  const answering =
    [...s.rows].reverse().find((r) => r.kind === 'rx' && r.register !== 'Protocol')
      ?.text ?? null

  const send = () => {
    const text = draft.trim()
    if (!text) return
    s.send(text)
    setDraft('')
    setComposing(false)
  }

  const readouts: (Readout | null)[] = [
    s.now ? { key: 'clock', label: 'desk', value: hhmm(s.now) } : null,
    s.nextCall ? { key: 'sked', label: 'next sked', value: hhmm(s.nextCall) } : null,
  ]

  const given = s.opening ? cardsAsGiven(s.opening.cards) : []

  return (
    <div
      className={styles.desk}
      style={{ height: `calc(100dvh - ${keyboard}px)` }}
      data-composing={composing || undefined}
    >
      <header className={styles.rail}>
        <Link to={s.scenario ? `/p/${s.scenario.id}` : '/'} className={styles.back}>
          ‹
        </Link>
        <div className={styles.station}>{s.station}</div>
        <div className={styles.clock}>{s.now ? hhmm(s.now) : ''}</div>
        {given.length > 0 && (
          <button className={styles.tab} onClick={() => setSheet('paper')}>
            paper
          </button>
        )}
        <button
          className={styles.tab}
          onClick={() => setSheet((v) => (v === 'book' ? 'none' : 'book'))}
          aria-expanded={sheet === 'book'}
        >
          book
        </button>
      </header>

      <InstrumentStrip readouts={readouts} />

      {/*
        `standing` (§2.5) stays unpassed for the same reason it does on the
        desktop surface: the server exposes no node data or past-the-chain state,
        and an invented reassurance is worse than the silence it papers over.
      */}
      <Traffic rows={s.rows} working={s.working} />

      {s.terminated ? (
        <div className={styles.ended}>
          — {s.terminationReason ?? 'the channel closes'} —
        </div>
      ) : composing ? (
        /*
         * Writing. The draft gets the room and the transcript gives it up —
         * except for the one line being answered, which Compose pins. Focus
         * enters this; blur, Escape and sending all leave it, and the draft
         * survives all three, so it is a posture and not a mode.
         */
        <Compose
          value={draft}
          onChange={setDraft}
          onSend={send}
          onExit={() => setComposing(false)}
          answering={answering}
        />
      ) : (
        <Outbox
          value={draft}
          onChange={setDraft}
          onFocus={() => setComposing(true)}
          onSend={send}
        />
      )}

      {sheet === 'paper' && (
        <Dossier given={given} received={[]} onClose={() => setSheet('none')} />
      )}

      {sheet === 'book' && (
        <div className={styles.sheet}>
          <header className={styles.sheetHead}>
            <h2 className={styles.sheetTitle}>{t('desk.book')}</h2>
            <button
              className={styles.close}
              onClick={() => setSheet('none')}
              aria-label={t('desk.book.close')}
            >
              ×
            </button>
          </header>
          <div className={styles.sheetBody}>
            <Book
              now={s.now}
              arrangement={s.arrangement}
              onArrange={s.arrange}
              nextCall={s.nextCall}
              onSetNextCall={s.setNextCall}
              plan={s.plan}
              onSetPlan={s.setPlan}
              onJump={s.jump}
              onSignOff={s.signOff}
              jumpBusy={s.jumpBusy}
              skedBusy={s.skedBusy}
              skedLabel={s.skedLabel}
              disabled={s.terminated}
            />
          </div>
        </div>
      )}
    </div>
  )
}
